import { VoiceAction } from '@community_dev/types/lib/api/InfobipVoice'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form, Input, Modal, notification, Select } from 'antd'
import type { JSX } from 'react'

import { postVoiceAction, VoiceOptions, VoiceSetup } from 'api/infobipVoice'
import { VOICE_ACTION_LABELS, VOICE_ACTION_PAYLOAD_PLACEHOLDERS } from 'constants/infobip-voice'
import { QUERIES } from 'constants/queries'

const { Option } = Select
const { TextArea } = Input

type VoiceActionModalProps = {
  clientId: string
  currentSetup: VoiceSetup | undefined
  onClose: () => void
  open: boolean
  options: VoiceOptions
  phoneNumber: string
}

type FormValues = {
  action: VoiceAction
  actionPayload: string
  description: string
}

export const VoiceActionModal = ({
  clientId,
  currentSetup,
  onClose,
  open,
  options,
  phoneNumber,
}: VoiceActionModalProps): JSX.Element => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const selectedAction = Form.useWatch('action', form)

  const actionEntries = Object.entries(options.actions ?? {}) as [VoiceAction, string][]

  const initialValues: Partial<FormValues> = {
    action: currentSetup?.action,
    actionPayload: currentSetup?.actionPayload ?? '',
    description: currentSetup?.description ?? '',
  }

  const { mutate: submit, isLoading } = useMutation({
    mutationFn: postVoiceAction,
    onSuccess: () => {
      notification.success({
        message: 'Voice action assigned successfully',
        placement: 'bottomRight',
        duration: 2,
      })
      queryClient.invalidateQueries([QUERIES.VOICE_STATUS, clientId])
      queryClient.invalidateQueries([QUERIES.PROVISIONING_STATUS, clientId])
      onClose()
    },
    onError: () => {
      notification.error({
        message: 'Failed to assign voice action',
        placement: 'bottomRight',
        duration: 2,
      })
    },
  })

  const handleSubmit = (values: FormValues) => {
    submit({
      clientId,
      phoneNumber,
      action: values.action,
      actionPayload: values.actionPayload.trim(),
      description: values.description.trim(),
    })
  }

  const selectedDescription = selectedAction ? options.actions?.[selectedAction] : undefined
  const payloadPlaceholder =
    (selectedAction && VOICE_ACTION_PAYLOAD_PLACEHOLDERS[selectedAction]) || 'Action-specific payload'

  return (
    <Modal
      confirmLoading={isLoading}
      destroyOnHidden
      okText="Assign"
      onCancel={onClose}
      onOk={() => form.submit()}
      open={open}
      title={`Customize Voice Action — ${phoneNumber}`}
    >
      <Form form={form} initialValues={initialValues} layout="vertical" onFinish={handleSubmit} preserve={false}>
        <Form.Item
          extra={selectedDescription}
          label="Action"
          name="action"
          rules={[{ required: true, message: 'Please select an action' }]}
        >
          <Select placeholder="Select a voice action">
            {actionEntries.map(([action]) => (
              <Option key={action} value={action}>
                {VOICE_ACTION_LABELS[action] || action}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Action Payload"
          name="actionPayload"
          rules={[{ required: true, message: 'Please enter an action payload' }]}
        >
          <Input placeholder={payloadPlaceholder} />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[
            { required: true, message: 'Please enter a description' },
            { max: 255, message: 'Description must be 255 characters or less' },
          ]}
        >
          <TextArea maxLength={255} placeholder="Human-readable description of this voice setup" rows={3} showCount />
        </Form.Item>
      </Form>
    </Modal>
  )
}
